import React, { useState, useMemo, useEffect, useCallback } from 'react';
import './OnlinePlayersPanel.css';

const API = process.env.REACT_APP_SERVER_URL || '';
const headers = () => ({
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const SORT_OPTIONS = [
  { key: 'name',  label: 'Name' },
  { key: 'level', label: 'Level' },
  { key: 'class', label: 'Class' },
  { key: 'area',  label: 'Area' },
];

const OnlinePlayersPanel = ({
  onlinePlayers = [],
  character,
  onWhisper,
  onSendMail,
  onInspect,
  onFellowshipInvite,
  onOpenGuildManage,
}) => {
  const [activeTab, setActiveTab] = useState('online'); // online | friends | guild
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [expanded, setExpanded] = useState(null);

  // Friends state
  const [friends, setFriends] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [friendsLoaded, setFriendsLoaded] = useState(false);
  const [addFriendName, setAddFriendName] = useState('');
  const [blockName, setBlockName] = useState('');
  const [showBlocked, setShowBlocked] = useState(false);

  // Guild state
  const [guild, setGuild] = useState(null);
  const [guildLoaded, setGuildLoaded] = useState(false);

  // Flash messages
  const [flash, setFlash] = useState(null);

  const showFlash = (msg, isError = false) => {
    setFlash({ msg, isError });
    setTimeout(() => setFlash(null), 3000);
  };

  // Build set of online player IDs for quick lookup
  const onlineIds = useMemo(() => {
    const ids = new Set();
    onlinePlayers.forEach(p => ids.add(p.id));
    return ids;
  }, [onlinePlayers]);

  // ── Friends fetching ──
  const fetchFriends = useCallback(async () => {
    try {
      const [fRes, bRes] = await Promise.all([
        fetch(`${API}/api/social/friends`, { headers: headers() }),
        fetch(`${API}/api/social/blocked`, { headers: headers() })
      ]);
      const fData = await fRes.json();
      const bData = await bRes.json();
      setFriends(fData.friends || []);
      setBlocked(bData.blocked || []);
    } catch (e) { console.error('Error fetching friends:', e); }
    finally { setFriendsLoaded(true); }
  }, []);

  // ── Guild fetching ──
  const fetchGuild = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/guild/my`, { headers: headers() });
      const data = await res.json();
      setGuild(res.ok && data.guild ? data.guild : null);
    } catch (e) { console.error('Error fetching guild:', e); }
    finally { setGuildLoaded(true); }
  }, []);

  // Lazy-load data when tab is first selected
  useEffect(() => {
    if (activeTab === 'friends' && !friendsLoaded) fetchFriends();
    if (activeTab === 'guild' && !guildLoaded) fetchGuild();
  }, [activeTab, friendsLoaded, guildLoaded, fetchFriends, fetchGuild]);

  // Refresh friends / guild periodically when their tab is active (every 30s)
  useEffect(() => {
    if (activeTab === 'friends') {
      const iv = setInterval(fetchFriends, 30000);
      return () => clearInterval(iv);
    }
    if (activeTab === 'guild') {
      const iv = setInterval(fetchGuild, 30000);
      return () => clearInterval(iv);
    }
  }, [activeTab, fetchFriends, fetchGuild]);

  // ── Friend / Block actions ──
  const addFriend = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/social/friend/add`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ characterName: addFriendName })
      });
      const data = await res.json();
      if (!res.ok) { showFlash(data.error, true); return; }
      showFlash(`${addFriendName} added as friend!`);
      setAddFriendName('');
      fetchFriends();
    } catch (e) { showFlash('Network error', true); }
  };

  const removeFriend = async (friendId, name) => {
    try {
      const res = await fetch(`${API}/api/social/friend/remove`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ targetId: friendId })
      });
      if (res.ok) { showFlash(`${name} removed`); fetchFriends(); }
    } catch (e) { showFlash('Error', true); }
  };

  const blockPlayer = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/social/block`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ characterName: blockName })
      });
      const data = await res.json();
      if (!res.ok) { showFlash(data.error, true); return; }
      showFlash(`${blockName} blocked`);
      setBlockName('');
      fetchFriends();
    } catch (e) { showFlash('Network error', true); }
  };

  const unblock = async (blockedId, name) => {
    try {
      const res = await fetch(`${API}/api/social/unblock`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ targetId: blockedId })
      });
      if (res.ok) { showFlash(`${name} unblocked`); fetchFriends(); }
    } catch (e) { showFlash('Error', true); }
  };

  // ── Sorting / filtering logic (shared) ──
  const sortPlayers = (players) => {
    let list = [...players];
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      list = list.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.class?.toLowerCase().includes(q) ||
        p.current_area?.toLowerCase().includes(q) ||
        p.guild_tag?.toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      let valA, valB;
      switch (sortBy) {
        case 'level':
          valA = a.level || 0;
          valB = b.level || 0;
          return sortAsc ? valA - valB : valB - valA;
        case 'class':
          valA = (a.advanced_class || a.class || '').toLowerCase();
          valB = (b.advanced_class || b.class || '').toLowerCase();
          break;
        case 'area':
          valA = (a.current_area || '').toLowerCase();
          valB = (b.current_area || '').toLowerCase();
          break;
        default:
          valA = (a.name || '').toLowerCase();
          valB = (b.name || '').toLowerCase();
          break;
      }
      if (sortBy !== 'level') {
        if (valA < valB) return sortAsc ? -1 : 1;
        if (valA > valB) return sortAsc ? 1 : -1;
        return 0;
      }
      return 0;
    });
    return list;
  };

  // ── Filtered lists ──
  const filteredOnline = useMemo(() => {
    return sortPlayers(onlinePlayers.filter(p => p.id !== character?.id));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlinePlayers, character, searchText, sortBy, sortAsc]);

  const friendsList = useMemo(() => {
    // Enrich friends with online info, sort online first then offline
    const enriched = friends.map(f => ({
      ...f,
      id: f.friend_id || f.id,
      isOnline: onlineIds.has(f.friend_id || f.id),
      // Merge live data from onlinePlayers if they're online
      ...(onlineIds.has(f.friend_id || f.id)
        ? onlinePlayers.find(op => op.id === (f.friend_id || f.id)) || {}
        : {}),
      // Keep friend record fields as fallback
      name: f.name,
      level: f.level,
      class: f.class,
      advanced_class: f.advanced_class,
    }));
    // Apply search filter
    let list = enriched;
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      list = list.filter(p => p.name?.toLowerCase().includes(q));
    }
    // Sort: online first, then alphabetically
    list.sort((a, b) => {
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    return list;
  }, [friends, onlineIds, onlinePlayers, searchText]);

  const guildMembers = useMemo(() => {
    if (!guild || !guild.members) return [];
    const enriched = guild.members.map(m => ({
      ...m,
      isOnline: onlineIds.has(m.id),
      // Merge live data if online
      ...(onlineIds.has(m.id) ? onlinePlayers.find(op => op.id === m.id) || {} : {}),
      name: m.name,
      level: m.level,
      class: m.class,
      advanced_class: m.advanced_class,
      guild_rank: m.guild_rank,
    }));
    let list = enriched;
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      list = list.filter(p => p.name?.toLowerCase().includes(q));
    }
    // Sort: online first, then by rank, then name
    const rankOrder = { leader: 0, officer: 1, member: 2 };
    list.sort((a, b) => {
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
      const ra = rankOrder[a.guild_rank] ?? 2;
      const rb = rankOrder[b.guild_rank] ?? 2;
      if (ra !== rb) return ra - rb;
      return (a.name || '').localeCompare(b.name || '');
    });
    return list;
  }, [guild, onlineIds, onlinePlayers, searchText]);

  // ── Notification counts ──
  const onlineFriendsCount = useMemo(() => {
    return friends.filter(f => onlineIds.has(f.friend_id || f.id)).length;
  }, [friends, onlineIds]);

  const onlineGuildCount = useMemo(() => {
    if (!guild || !guild.members) return 0;
    return guild.members.filter(m => onlineIds.has(m.id) && m.id !== character?.id).length;
  }, [guild, onlineIds, character]);

  const handleSortToggle = (key) => {
    if (sortBy === key) setSortAsc(!sortAsc);
    else { setSortBy(key); setSortAsc(true); }
  };

  const toggleExpand = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 'leader': return '👑';
      case 'officer': return '⚔️';
      default: return '🛡️';
    }
  };

  // ── Player row renderer (shared between Online & Friends) ──
  const renderPlayerRow = (player, opts = {}) => {
    const { showOnlineStatus = false, showRank = false, showRemoveFriend = false } = opts;
    const isOnline = showOnlineStatus ? (player.isOnline ?? onlineIds.has(player.id)) : true;

    return (
      <div key={player.id || player.friend_id} className={`online-player-row ${showOnlineStatus && !isOnline ? 'offline-row' : ''}`}>
        <div className="online-player-main" onClick={() => toggleExpand(player.id || player.friend_id)}>
          {showOnlineStatus && (
            <span className={`online-status-dot ${isOnline ? 'dot-online' : 'dot-offline'}`} />
          )}
          {showRank && player.guild_rank && (
            <span className="online-rank-icon">{getRankIcon(player.guild_rank)}</span>
          )}
          <span className="online-player-name">
            {!showRank && player.guild_tag && <span className="online-guild-tag">[{player.guild_tag}]</span>}
            {player.name}
          </span>
          <span className="online-player-level">{player.level || '?'}</span>
          <span className="online-player-class">{player.advanced_class || player.class || '—'}</span>
          {!showRank && <span className="online-player-area">{isOnline ? (player.current_area || '—') : 'Offline'}</span>}
        </div>

        {expanded === (player.id || player.friend_id) && (
          <div className="online-player-actions">
            {isOnline && player.current_area && (
              <span className="online-expanded-area">📍 {player.current_area}</span>
            )}
            <div className="online-action-btns">
              {isOnline && onWhisper && (
                <button className="online-action-btn whisper-btn" onClick={() => onWhisper(player)} title="Whisper">
                  💬 Whisper
                </button>
              )}
              {isOnline && onSendMail && (
                <button className="online-action-btn mail-btn" onClick={() => onSendMail(player)} title="Mail">
                  ✉️ Mail
                </button>
              )}
              {onInspect && (
                <button className="online-action-btn inspect-btn" onClick={() => onInspect(player.id || player.friend_id)} title="Inspect">
                  🔍 Inspect
                </button>
              )}
              {isOnline && onFellowshipInvite && player.current_area === character?.current_area && player.id !== character?.id && (
                <button className="online-action-btn fellowship-btn" onClick={() => onFellowshipInvite(player.id)} title="Fellowship invite">
                  🤝 Invite
                </button>
              )}
              {showRemoveFriend && (
                <button className="online-action-btn remove-btn" onClick={() => removeFriend(player.friend_id || player.id, player.name)} title="Remove friend">
                  ✕ Remove
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="online-players-panel">
      {/* ── Tab Bar ── */}
      <div className="online-tabs">
        <button
          className={`online-tab ${activeTab === 'online' ? 'active' : ''}`}
          onClick={() => setActiveTab('online')}
        >
          🌐 Online
          <span className="tab-badge">{onlinePlayers.length}</span>
        </button>
        <button
          className={`online-tab ${activeTab === 'friends' ? 'active' : ''}`}
          onClick={() => setActiveTab('friends')}
        >
          ❤️ Friends
          {onlineFriendsCount > 0 && (
            <span className="tab-badge tab-badge-notify">{onlineFriendsCount}</span>
          )}
        </button>
        <button
          className={`online-tab ${activeTab === 'guild' ? 'active' : ''}`}
          onClick={() => setActiveTab('guild')}
        >
          ⚔️ Guild
          {onlineGuildCount > 0 && (
            <span className="tab-badge tab-badge-notify">{onlineGuildCount}</span>
          )}
        </button>
      </div>

      {/* ── Flash message ── */}
      {flash && (
        <div className={`online-flash ${flash.isError ? 'flash-error' : 'flash-success'}`}>
          {flash.msg}
        </div>
      )}

      {/* ══════════ ONLINE TAB ══════════ */}
      {activeTab === 'online' && (
        <>
          <div className="online-search">
            <input
              type="text" placeholder="Search players..."
              value={searchText} onChange={(e) => setSearchText(e.target.value)}
              className="online-search-input"
            />
          </div>
          <div className="online-sort-row">
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.key}
                className={`online-sort-btn ${sortBy === opt.key ? 'active' : ''}`}
                onClick={() => handleSortToggle(opt.key)}
              >
                {opt.label}
                {sortBy === opt.key && <span className="sort-arrow">{sortAsc ? ' ▲' : ' ▼'}</span>}
              </button>
            ))}
          </div>
          <div className="online-list">
            {filteredOnline.length === 0 && (
              <div className="online-empty">
                {searchText ? 'No matches found' : 'No other players online'}
              </div>
            )}
            {filteredOnline.map(player => renderPlayerRow(player))}
          </div>
        </>
      )}

      {/* ══════════ FRIENDS TAB ══════════ */}
      {activeTab === 'friends' && (
        <>
          <div className="online-search">
            <form onSubmit={addFriend} className="online-add-form">
              <input
                type="text" placeholder="Add friend by name..."
                value={addFriendName} onChange={(e) => setAddFriendName(e.target.value)}
                className="online-search-input" required
              />
              <button type="submit" className="online-add-btn">+</button>
            </form>
          </div>
          <div className="online-list">
            {!friendsLoaded && <div className="online-empty">Loading...</div>}
            {friendsLoaded && friendsList.length === 0 && (
              <div className="online-empty">
                {searchText ? 'No matches' : 'No friends added yet. Add friends by name above!'}
              </div>
            )}
            {friendsList.map(player => renderPlayerRow(player, {
              showOnlineStatus: true,
              showRemoveFriend: true,
            }))}
          </div>

          {/* Blocked section (collapsible) */}
          <div className="online-blocked-toggle" onClick={() => { setShowBlocked(!showBlocked); if (!showBlocked && !friendsLoaded) fetchFriends(); }}>
            <span>🚫 Blocked ({blocked.length})</span>
            <span className="toggle-arrow">{showBlocked ? '▼' : '▶'}</span>
          </div>
          {showBlocked && (
            <div className="online-blocked-section">
              <form onSubmit={blockPlayer} className="online-add-form">
                <input
                  type="text" placeholder="Block player by name..."
                  value={blockName} onChange={(e) => setBlockName(e.target.value)}
                  className="online-search-input" required
                />
                <button type="submit" className="online-add-btn block-btn">🚫</button>
              </form>
              <div className="online-blocked-list">
                {blocked.length === 0 && <div className="online-empty">No blocked players.</div>}
                {blocked.map(b => (
                  <div key={b.blocked_id || b.id} className="online-blocked-row">
                    <span className="online-player-name">{b.name}</span>
                    <button className="online-action-btn unblock-btn" onClick={() => unblock(b.blocked_id || b.id, b.name)}>
                      Unblock
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════ GUILD TAB ══════════ */}
      {activeTab === 'guild' && (
        <>
          {!guildLoaded && <div className="online-empty" style={{ padding: '1rem' }}>Loading...</div>}
          {guildLoaded && !guild && (
            <div className="online-empty" style={{ padding: '1rem', textAlign: 'center' }}>
              <p>You are not in a guild.</p>
              {onOpenGuildManage && (
                <button className="online-action-btn guild-manage-btn" onClick={onOpenGuildManage} style={{ marginTop: '0.5rem' }}>
                  🏰 Create or Join a Guild
                </button>
              )}
            </div>
          )}
          {guildLoaded && guild && (
            <>
              <div className="online-guild-header">
                <span className="online-guild-name">[{guild.tag}] {guild.name}</span>
                {onOpenGuildManage && (
                  <button className="online-guild-manage-btn" onClick={onOpenGuildManage} title="Guild management">
                    ⚙️
                  </button>
                )}
              </div>
              <div className="online-search">
                <input
                  type="text" placeholder="Search members..."
                  value={searchText} onChange={(e) => setSearchText(e.target.value)}
                  className="online-search-input"
                />
              </div>
              <div className="online-list">
                {guildMembers.length === 0 && (
                  <div className="online-empty">
                    {searchText ? 'No matches' : 'No members found'}
                  </div>
                )}
                {guildMembers.map(member => renderPlayerRow(member, {
                  showOnlineStatus: true,
                  showRank: true,
                }))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default OnlinePlayersPanel;
